import { useId, useState, type ChangeEvent } from 'react';

import { ButtonGroup } from '../../../../../primitives/button-group';
import { Input } from '../../../../../primitives/input';
import { Select, SelectContent, SelectItem, SelectTrigger } from '../../../../../primitives/select';

export interface InputNumberWithStepsProps {
  steps?: number[];
  value?: number | null;
  defaultValue?: number | null;
  onChange?: (value: number | null) => void;
  placeholder?: string;
  min?: number;
  max?: number;
  step?: number;
  disabled?: boolean;
  name?: string;
  id?: string;
  ariaLabel?: string;
  unit?: string;
}

export function InputNumberWithSteps({
  steps = [],
  value,
  defaultValue = null,
  onChange,
  placeholder,
  min,
  max,
  step,
  disabled = false,
  name,
  id,
  ariaLabel,
  unit,
}: InputNumberWithStepsProps) {
  const generatedId = useId();
  const inputId = id ?? `input-number-${generatedId}`;
  const selectId = `${inputId}-steps`;

  const [internalValue, setInternalValue] = useState<number | null>(defaultValue);

  const isControlled = value !== undefined;
  const currentValue = isControlled ? (value ?? null) : internalValue;

  const commit = (next: number | null) => {
    if (!isControlled) {
      setInternalValue(next);
    }
    onChange?.(next);
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const raw = event.target.value;

    if (raw === '') {
      commit(null);
      return;
    }

    const parsed = Number(raw);

    if (Number.isNaN(parsed)) {
      return;
    }

    commit(parsed);
  };

  const handleInputBlur = () => {
    if (currentValue === null) {
      return;
    }

    const boundedValue = Math.min(max ?? currentValue, Math.max(min ?? currentValue, currentValue));

    if (boundedValue !== currentValue) {
      commit(boundedValue);
    }
  };

  const handleSelectChange = (raw: string) => {
    commit(Number(raw));
  };

  // The select reflects the value only if it matches one of the steps.
  const selectValue =
    currentValue !== null && steps.includes(currentValue) ? String(currentValue) : undefined;

  return (
    <ButtonGroup className="w-full">
      <Input
        disabled={disabled}
        id={inputId}
        max={max}
        min={min}
        name={name}
        aria-label={ariaLabel}
        onBlur={handleInputBlur}
        onChange={handleInputChange}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
          }
        }}
        placeholder={placeholder}
        sizesx="sm"
        step={step}
        type="number"
        value={currentValue === null ? '' : currentValue}
      />

      {steps.length > 0 ? (
        <Select disabled={disabled} onValueChange={handleSelectChange} value={selectValue ?? ''}>
          <SelectTrigger
            id={selectId}
            className="w-[2.5rem] justify-end rounded-r-full! p-0 pr-3"
            size="sm"
            variant="outline"
          ></SelectTrigger>
          <SelectContent align="end" className="h-[12.5rem] w-[5rem] min-w-[5rem]">
            {steps.map((item) => (
              <SelectItem key={item} value={String(item)} size="sm">
                {item}
                {unit}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : null}
    </ButtonGroup>
  );
}
